import { useEffect, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useAtom } from 'jotai'
import { Bug, Minus, X } from 'lucide-react'
import { Switch } from '@duck/ui'
import { useKonamiCode } from '@/hooks/useKonamiCode'
import { useAuth } from '@/hooks/useAuth'
import {
  debugBubblePositionAtom,
  debugDevEnabledAtom,
  debugPanelStateAtom,
  germanUiEnabledAtom,
  pythonNotebookV2EnabledAtom,
} from '@/store/debugAtoms'
import i18n from '@/modules/i18n/i18n'
import { applyDocumentDirection } from '@/modules/i18n'
import { getEffectiveUiLanguage } from '@/modules/i18n/uiLanguageVisibility'
import { AccessGroupsTester } from './AccessGroupsTester'

// Funky dashed accent shared by the open panel and the minimized bubble.
const FUNKY_BORDER = 'border-2 border-dashed border-fuchsia-500/70'

const BUBBLE_SIZE = 36 // px, matches size-9
const BUBBLE_MARGIN = 16 // px, matches right-4 / top-4
// Pointer travel (px) beyond which a press counts as a drag rather than a click.
const DRAG_THRESHOLD = 4

const clampToViewport = (x: number, y: number) => ({
  x: Math.min(Math.max(x, 0), Math.max(window.innerWidth - BUBBLE_SIZE, 0)),
  y: Math.min(Math.max(y, 0), Math.max(window.innerHeight - BUBBLE_SIZE, 0)),
})

/**
 * Hidden internal debug panel, summoned via the Konami code (↑↑↓↓←→←→ b a).
 * A fixed floating widget pinned to the top-right of every page. Copy is
 * intentionally hardcoded English — not translated. Desktop-only.
 */
export function DebugPanel() {
  const [state, setState] = useAtom(debugPanelStateAtom)
  const [germanEnabled, setGermanEnabled] = useAtom(germanUiEnabledAtom)
  const [debugDevEnabled, setDebugDevEnabled] = useAtom(debugDevEnabledAtom)
  const [notebookV2, setNotebookV2] = useAtom(pythonNotebookV2EnabledAtom)
  const [bubblePosition, setBubblePosition] = useAtom(debugBubblePositionAtom)
  const { isAdmin } = useAuth()

  // Drag bookkeeping for the minimized bubble. Position is written straight to
  // the element's style during the drag and committed to the atom on release,
  // so we don't re-render (and hit localStorage) on every pointer move.
  const bubbleRef = useRef<HTMLButtonElement>(null)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
    moved: boolean
  } | null>(null)

  const onBubblePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || !bubbleRef.current) return
    const rect = bubbleRef.current.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    }
    bubbleRef.current.setPointerCapture(event.pointerId)
  }

  const onBubblePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId || !bubbleRef.current) return
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    drag.moved = true
    const { x, y } = clampToViewport(drag.originX + dx, drag.originY + dy)
    const { style } = bubbleRef.current
    style.left = `${x}px`
    style.top = `${y}px`
    style.right = 'auto'
  }

  const onBubblePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    bubbleRef.current?.releasePointerCapture(event.pointerId)
    if (!drag.moved) {
      setState('open')
      return
    }
    const rect = bubbleRef.current?.getBoundingClientRect()
    if (rect) setBubblePosition(clampToViewport(rect.left, rect.top))
  }

  // Konami toggles between open and closed; from minimized it re-opens.
  useKonamiCode(() => setState((prev) => (prev === 'open' ? 'closed' : 'open')))

  // Reconcile the active language whenever the German-UI flag FLIPS at runtime,
  // non-destructively: enabling restores a stored 'de'; disabling reverts active
  // to 'en'. The stored preference is never written here.
  //
  // Skip the initial mount run: on mount the active language is already correct
  // (i18n init + LanguageSync), so reconciling here would clobber a URL-derived
  // language — e.g. on a prerendered /he page, localStorage is empty so
  // getEffectiveUiLanguage() is 'en', which would force <html lang="en"> and
  // break the prerender's lang assertion. Only actual flag toggles should act.
  const didMountLangReconcile = useRef(false)
  useEffect(() => {
    if (!didMountLangReconcile.current) {
      didMountLangReconcile.current = true
      return
    }
    const target = getEffectiveUiLanguage()
    if (i18n.language !== target) {
      void i18n.changeLanguage(target).then(() => applyDocumentDirection(target))
    }
  }, [germanEnabled])

  if (state === 'closed') return null

  if (state === 'minimized') {
    const positionStyle = bubblePosition
      ? { left: bubblePosition.x, top: bubblePosition.y, right: 'auto' }
      : { right: BUBBLE_MARGIN, top: BUBBLE_MARGIN }
    return (
      <button
        ref={bubbleRef}
        type="button"
        dir="ltr"
        aria-label="Open debug panel (drag to move)"
        title="Drag to move"
        onPointerDown={onBubblePointerDown}
        onPointerMove={onBubblePointerMove}
        onPointerUp={onBubblePointerUp}
        onPointerCancel={onBubblePointerUp}
        style={positionStyle}
        className={`fixed z-[60] flex size-9 cursor-grab touch-none items-center justify-center rounded-full ${FUNKY_BORDER} bg-background/95 text-fuchsia-600 shadow-lg backdrop-blur transition-transform hover:scale-110 hover:rotate-3 active:cursor-grabbing dark:text-fuchsia-400`}
      >
        <Bug className="size-4" />
      </button>
    )
  }

  return (
    <div
      dir="ltr"
      className={`fixed right-4 top-4 z-[60] w-64 rounded-xl ${FUNKY_BORDER} bg-background/95 font-mono shadow-xl backdrop-blur`}
    >
      <header className="flex items-center justify-between gap-2 rounded-t-[0.625rem] bg-gradient-to-r from-fuchsia-500/20 to-cyan-500/20 px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-bold tracking-wide text-fuchsia-600 dark:text-fuchsia-400">
          <Bug className="size-4" />
          debug
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Minimize debug panel"
            onClick={() => setState('minimized')}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
          >
            <Minus className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Close debug panel"
            onClick={() => setState('closed')}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
          >
            <X className="size-4" />
          </button>
        </div>
      </header>

      <div className="divide-y divide-border">
        <div className="flex items-center justify-between px-3 py-3">
          <span className="text-sm font-medium">debugDev</span>
          <Switch checked={debugDevEnabled} onCheckedChange={setDebugDevEnabled} />
        </div>
        <div className="flex items-center justify-between px-3 py-3">
          <span className="text-sm font-medium">German UI</span>
          <Switch checked={germanEnabled} onCheckedChange={setGermanEnabled} />
        </div>
        <div className="flex items-center justify-between gap-2 px-3 py-3">
          <span className="text-sm font-medium" title="Rebuilt Python notebook (Pyodide 314). Remounts an open notebook.">
            Python notebook v2
          </span>
          <Switch checked={notebookV2} onCheckedChange={setNotebookV2} />
        </div>
        {isAdmin && <AccessGroupsTester />}
      </div>
    </div>
  )
}
